/**
 * The editor's Setbacks tab: the clearances this design uses per roof edge type (eave, ridge, rake, hip, valley)
 * and around obstacles, from a fire-code preset or typed in. Changing them previews the zones live on the plan, and
 * sticky placement keeps panels inside the new usable area. "Use for this design" keeps them as the design's override
 * (audited); the next save recomputes setbacks, layout and quote with them.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { Alert, Badge, Button, NumberInput, Select, Spinner, useToast } from "../components/ui";
import { useIntlShape, useT } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { num } from "../lib/format";
import { Block } from "./SavingsPanel";
import type { useDesignSetbacks } from "./planHooks.ts";
import "./plan.css";

const EDGES: [keyof S["SetbackEdges"], MessageKey, MessageKey][] = [
  ["eave", "editor.setbacks.edge.eave", "editor.setbacks.edge.eaveHint"],
  ["ridge", "editor.setbacks.edge.ridge", "editor.setbacks.edge.ridgeHint"],
  ["rake", "editor.setbacks.edge.rake", "editor.setbacks.edge.rakeHint"],
  ["hip", "editor.setbacks.edge.hip", "editor.setbacks.edge.hipHint"],
  ["valley", "editor.setbacks.edge.valley", "editor.setbacks.edge.valleyHint"],
];

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** The tab's Apply: a save of a new version with today's rules (`blocked` while there are unsaved edits). */
export interface ApplySetbacks {
  run: () => void;
  pending: boolean;
  blocked: boolean;
}

export function SetbacksPanel({ designId, canEdit, setbacks, historical = false, apply }: { designId: string; canEdit: boolean; setbacks: ReturnType<typeof useDesignSetbacks>; historical?: boolean; apply?: ApplySetbacks }) {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const intl = useIntlShape();
  const pct = (fraction: number) => intl.formatNumber(fraction, { style: "percent", maximumFractionDigits: 0 });
  const { state, draft, setDraft, preview, previewing } = setbacks;
  const rules = draft ?? state?.effective ?? null;
  const presetOf = useMemo(() => {
    if (!state || !rules) return "custom";
    const hit = state.presets.find((p) => same(p.document, rules));
    if (hit) return hit.slug;
    return same(state.company, rules) ? "company" : "custom";
  }, [state, rules]);

  const put = useMutation({
    mutationFn: (body: S["DesignSetbacksWrite"]) => unwrap(api.PUT("/v1/designs/{design_id}/setbacks", { params: { path: { design_id: designId } }, body })),
    onSuccess: (r) => {
      qc.setQueryData(["design-setbacks", designId], r);
      setDraft(null);
      toast(t("editor.setbacks.kept"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const clear = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/designs/{design_id}/setbacks", { params: { path: { design_id: designId } } })),
    onSuccess: (r) => {
      qc.setQueryData(["design-setbacks", designId], r);
      setDraft(null);
      toast(t("editor.setbacks.cleared"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });

  if (setbacks.loading) return <Spinner label={t("editor.loading")} />;
  if (!state || !rules) return <Alert tone="warn" title={t("editor.setbacks.unavailable")} />;
  const edit = (patch: Partial<S["SetbacksSettings"]>) => setDraft({ ...rules, ...patch });
  const edge = (key: keyof S["SetbackEdges"], v: number | null) => edit({ edges: { ...rules.edges, [key]: Math.max(0, v ?? 0) } });
  const changed = draft != null && !same(draft, state.effective);
  const usable = preview?.summary.usable_area_m2;
  const roofArea = preview?.summary.roof_area_m2;

  return (
    <section className="stack plan-panel">
      <div className="row wrap">
        <span className="small muted">{t("editor.setbacks.using")}</span>
        {state.override ? <Badge tone="info">{t("editor.setbacks.source.design")}</Badge> : <Badge tone="ok" plain>{t("editor.setbacks.source.company")}</Badge>}
      </div>
      {state.applied === false && !historical && (
        <Alert
          tone="info"
          title={t("editor.setbacks.rulesChanged.title")}
          action={
            canEdit && apply ? (
              <Button size="sm" variant="primary" loading={apply.pending} disabled={apply.blocked} title={apply.blocked ? t("editor.setbacks.rulesChanged.saveFirst") : undefined} onClick={apply.run}>
                {t("editor.setbacks.rulesChanged.apply")}
              </Button>
            ) : null
          }
        >
          {t("editor.setbacks.rulesChanged.body")}
          {canEdit && apply?.blocked ? ` ${t("editor.setbacks.rulesChanged.saveFirst")}` : ""}
        </Alert>
      )}
      <Block title={t("editor.setbacks.preset")}>
        <Select
          ariaLabel={t("editor.setbacks.presetLabel")}
          value={presetOf}
          disabled={!canEdit}
          onChange={(slug) => {
            if (slug === "company") setDraft(state.company);
            else if (slug !== "custom") setDraft(state.presets.find((p) => p.slug === slug)!.document);
          }}
          options={[
            { value: "company", label: t("editor.setbacks.option.company") },
            ...state.presets.map((p) => ({ value: p.slug, label: p.name })),
            ...(presetOf === "custom" ? [{ value: "custom", label: t("editor.setbacks.option.custom") }] : []),
          ]}
        />
        {(() => {
          const p = state.presets.find((x) => x.slug === presetOf);
          return p ? (
            <p className="small muted">
              {p.description}
              {p.source ? ` (${p.source})` : ""}
            </p>
          ) : null;
        })()}
      </Block>
      <Block title={t("editor.setbacks.byEdge")}>
        <div className="edge-grid">
          {EDGES.map(([key, label, hint]) => (
            <label key={key} title={t(hint)}>
              <span>{t(label)}</span>
              <NumberInput value={rules.edges[key]} min={0} step={0.05} suffix="m" disabled={!canEdit} onChange={(v) => edge(key, v)} />
            </label>
          ))}
          <label title={t("editor.setbacks.obstacleHint")}>
            <span>{t("editor.setbacks.obstacle")}</span>
            <NumberInput value={rules.obstruction_m} min={0} step={0.05} suffix="m" disabled={!canEdit} onChange={(v) => edit({ obstruction_m: Math.max(0, v ?? 0) })} />
          </label>
          <label title={t("editor.setbacks.pathwaysHint")}>
            <span>{t("editor.setbacks.pathways")}</span>
            <NumberInput value={rules.pathways.count} min={0} step={1} disabled={!canEdit} onChange={(v) => edit({ pathways: { ...rules.pathways, count: Math.max(0, Math.round(v ?? 0)) } })} />
          </label>
          <label>
            <span>{t("editor.setbacks.pathwayWidth")}</span>
            <NumberInput value={rules.pathways.width_m} min={0} step={0.05} suffix="m" disabled={!canEdit} onChange={(v) => edit({ pathways: { ...rules.pathways, width_m: Math.max(0, v ?? 0) } })} />
          </label>
        </div>
        {rules.ridge_by_coverage?.length ? (
          <p className="small muted">
            {t("editor.setbacks.ridgeByCoverage", { rules: intl.formatList(rules.ridge_by_coverage.map((r) => t("editor.setbacks.ridgeRule", { ridge: num(r.ridge_m, 3), coverage: pct(r.max_array_coverage) })), { type: "unit" }) })}
          </p>
        ) : null}
        {rules.overrides.length > 0 && <p className="small muted">{t("editor.setbacks.conditional", { rules: intl.formatList(rules.overrides.map((o) => o.name.replace(/_/g, " ")), { type: "unit" }) })}</p>}
      </Block>
      {(preview || previewing) && (
        <p className="small">
          {previewing ? t("editor.setbacks.updating") : usable != null && roofArea ? t("editor.setbacks.usable", { usable: num(usable, 1), roof: num(roofArea, 1), percent: pct(usable / roofArea) }) : null}
        </p>
      )}
      {canEdit && (
        <div className="row wrap">
          <Button variant="primary" disabled={!draft || (!changed && !!state.override) || (presetOf === "company" && !state.override)} loading={put.isPending} onClick={() => put.mutate(presetOf !== "custom" && presetOf !== "company" ? { preset: presetOf } : { rules })}>
            {t("editor.setbacks.useForDesign")}
          </Button>
          {draft && (
            <Button variant="ghost" onClick={() => setDraft(null)}>
              {t("editor.setbacks.discard")}
            </Button>
          )}
          {state.override && (
            <Button variant="ghost" loading={clear.isPending} onClick={() => clear.mutate()}>
              {t("editor.setbacks.useCompany")}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
